/* One piece of work, and the step that is waiting.
 *
 * Phone first for replies and approvals (UX bar): the step that needs this
 * person sits at the top, and its form asks only for what that step takes —
 * the reply's values, hours and files for a submission, a reason for a
 * request for information, a sentence for sending something back. Everything
 * goes in one request, so a reply never lands without its hours.
 *
 * Below it, laid out like a ticket (owner, 2026-10-08: "the jobs UI has the content lying here
 * and there"): the work itself on the left — its notes, its rows (a job), the hours, the files
 * and the conversation — and a Details card on the right, kept in view, with who, when and the
 * pack's fields. Sending to the client and the invoice are actions taken now and then, so they
 * fold away at the bottom instead of standing between a person and the work. A revise loop keeps
 * its history — every send-back is a comment and the revision number climbs.
 */
import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Failed, Loading, NoAccess, ScreenState, useToast } from '@jackdesk/shell'
import { newIdempotencyKey } from '../api'
import type { ErpError } from '../api'
import { useErp } from '../context'
import { formatDate, formatDatetime, formatNumber } from '../format'
import type {
  InvoiceDraft, Kind, PackField, ReleaseState, TeamsConnection, WorkDetail, WorkRow, WorkStep,
  ReleaseSend, SentFile,
} from './types'
import { PackFieldInput, usePersonName } from './Pickers'
import { PeopleChange } from './PeopleChange'
import { Timer, hoursSince } from './Timer'
import { usePacksActive } from './usePacks'
import {
  STATES, findKind, homeLabel, priorityOf, roleWord, stateLabel, stateTone, stepLabel, stepNeeds, whoWord,
} from './words'

export function WorkItem({ name, onOpen, onBack, onOpenInvoice, onSendFiles }: {
  name: string; onOpen: (name: string) => void; onBack: () => void
  /** Open an invoice in the ERP's own form. */
  onOpenInvoice?: (name: string) => void
  /** Release approved files on the host's channels (PK-42). */
  onSendFiles?: (release: ReleaseSend) => Promise<SentFile[]>
}) {
  const erp = useErp()
  const { active } = usePacksActive()
  const [detail, setDetail] = useState<WorkDetail | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)

  const load = useCallback((signal?: AbortSignal) => {
    setFailed(null)
    erp.workDetail(name, signal).then(setDetail).catch((e) => {
      if ((e as Error).name !== 'AbortError') setFailed(e as ErpError)
    })
  }, [erp, name])
  useEffect(() => {
    const controller = new AbortController()
    setDetail(null)
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  const nameOf = usePersonName()
  const [changingPeople, setChangingPeople] = useState(false)

  if (failed) {
    if (failed.status === 403) return <div className="page"><NoAccess what="this task" /></div>
    if (failed.status === 404) {
      return (
        <div className="page">
          <ScreenState title="This task is not here"
            detail="It may have been removed, or it belongs to a pack that is switched off."
            actions={<button className="btn" onClick={onBack}>Back to {homeLabel(active).toLowerCase()}</button>} />
        </div>
      )
    }
    return <div className="page"><Failed message={failed.message} requestId={failed.requestId} onRetry={() => load()} /></div>
  }
  if (!detail || !active) return <div className="page"><Loading rows={8} label="Opening the task" /></div>

  const found = findKind(active, detail.task.jd_kind)
  const kind = found?.kind
  const words = kind?.words ?? {}
  const t = detail.task
  const priority = priorityOf(t, kind)
  // Only what has been said: an unticked box or a zero is not a fact yet.
  const shownFields = (kind?.fields ?? []).filter((f) => {
    const v = t[f.fieldname]
    return v !== null && v !== undefined && v !== '' && v !== 0 && v !== '0'
  })

  const releasable = kind?.flow === 'review' && t.workflow_state === STATES.APPROVED
  return (
    <div className="page work-item">
      <header>
        <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
          <button className="btn btn-quiet btn-sm" onClick={onBack}>← {homeLabel(active)}</button>
          {detail.job ? (
            <button className="btn btn-quiet btn-sm" onClick={() => onOpen(detail.job!.name)}>
              ↑ {detail.job.subject}
            </button>
          ) : null}
        </div>
        <h1 style={{ marginTop: 6 }}>{t.subject}</h1>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          <span className="chip">{kind?.label ?? t.jd_kind}</span>
          <span className={`chip chip-${stateTone(shownState(t, kind))}`}>
            {stateLabel(shownState(t, kind), words, kind?.flow)}
          </span>
          {priority ? <span className="chip chip-warn">{priority}</span> : null}
          {t.overdue ? <span className="chip chip-danger">Late</span> : null}
          {t.jd_round ? <span className="chip">Revision {t.jd_round}</span> : null}
          {/* The timer, for whoever's turn it is (or whose clock already runs here). */}
          {!t.closed && (t.needs_me || detail.steps.length > 0 || detail.timer?.task === t.name)
            ? <Timer name={t.name} running={detail.timer} mode={detail.time_mode} onChanged={() => load()} />
            : null}
        </div>
      </header>

      {kind ? <NextStep detail={detail} kind={kind} onDone={() => load()} nameOf={nameOf} /> : null}

      <div className="work-body">
        <aside className="card card-pad work-side" aria-label="Details">
          <div className="work-side-h">Details</div>
          {t.project ? (
            <Fact label="Project" value={
              <>{t.project_title ?? t.project}{t.customer ? <div className="small muted">{t.customer}</div> : null}</>} />
          ) : null}
          {t.project_manager ? <Fact label="Project manager" value={nameOf(t.project_manager)} /> : null}
          {t.exp_end_date ? (
            <Fact label={words.deadline ?? 'Deadline'}
                  value={<span style={t.overdue ? { color: 'var(--danger)' } : undefined}>
                    {formatDate(t.exp_end_date)}{t.overdue ? ' · late' : ''}</span>} />
          ) : null}
          {t.assigned.length ? (
            <Fact label={words.makers ?? (kind?.flow === 'complete' ? 'Assigned to' : whoWord('maker', words))}
                  value={t.assigned.map(nameOf).join(', ')} />
          ) : null}
          {t.jd_checker ? <Fact label={whoWord('checker', words)} value={nameOf(t.jd_checker)} /> : null}
          {t.jd_reviewer ? <Fact label={whoWord('reviewer', words)} value={nameOf(t.jd_reviewer)} /> : null}
          {t.jd_approver ? <Fact label={whoWord('approver', words)} value={nameOf(t.jd_approver)} /> : null}
          {t.department ? <Fact label="Department" value={t.department} /> : null}
          {t.owner ? <Fact label="Raised by" value={nameOf(t.owner)} /> : null}
          {kind && detail.may_plan && !t.closed ? (
            changingPeople ? (
              <PeopleChange row={t} kind={kind} onCancel={() => setChangingPeople(false)}
                            onDone={() => { setChangingPeople(false); load() }} />
            ) : (
              <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => setChangingPeople(true)}>
                Change people
              </button>
            )
          ) : null}
          {shownFields.map((f) => <FieldFact key={f.name} field={f} value={t[f.fieldname]} />)}
          {detail.total_hours ? <Fact label="Hours logged" value={formatNumber(detail.total_hours, 2)} /> : null}
        </aside>

        <div className="work-main">
          {t.description ? (
            <section className="card card-pad" aria-label={words.description ?? 'Notes'}>
              <div className="small muted">{words.description ?? 'Notes'}</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{stripTags(String(t.description))}</div>
            </section>
          ) : null}

          {detail.rows.length ? (
            <Rows rows={detail.rows} kindOf={(r) => findKind(active, r.jd_kind)?.kind}
                  title={words.rows ?? 'Rows'} onOpen={onOpen} nameOf={nameOf}
                  mayPlan={Boolean(detail.may_plan) && !t.closed} onChanged={() => load()} />
          ) : null}

          {detail.hours.length ? <Hours detail={detail} nameOf={nameOf} /> : null}
          <Files detail={detail} onChanged={() => load()} />
          <Conversation detail={detail} onChanged={() => load()} nameOf={nameOf} />

          {/* Taken now and then: folded away, said in one line until opened. */}
          {releasable ? (
            <details className="card work-fold">
              <summary>Send to the client <span className="small muted">· approved, ready to go out</span></summary>
              <Release name={t.name} onChanged={() => load()} onSendFiles={onSendFiles} />
            </details>
          ) : null}
          {kind?.bills ? (
            <details className="card work-fold">
              <summary>Invoice <span className="small muted">· drafted from the approved {(words.rows ?? 'rows').toLowerCase()}</span></summary>
              <Invoice name={t.name} onOpenInvoice={onOpenInvoice} />
            </details>
          ) : null}
          {kind?.group && kind.may_create && t.project ? (
            <TeamsChannel name={t.name} current={detail.channel ?? ''} onChanged={() => load()} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** A kind with no steps of its own (a job) is where its rows have got to:
 *  ERPNext's status, which the gateway keeps in step with them. */
function shownState(t: WorkRow, kind: Kind | undefined): string | null | undefined {
  return kind && !kind.flow ? (t.status ?? t.workflow_state) : t.workflow_state
}

/** One line of the Details card: the label beside its value. */
function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="work-fact">
      <div className="k">{label}</div>
      <div className="v" title={typeof value === 'string' ? value : undefined}>{value}</div>
    </div>
  )
}

function FieldFact({ field, value }: { field: PackField; value: unknown }) {
  let shown: ReactNode = String(value)
  if (field.fieldtype === 'Check') shown = value ? 'Yes' : 'No'
  else if (field.fieldtype === 'Date') shown = formatDate(value)
  else if (field.fieldtype === 'Float') shown = formatNumber(value)
  else if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
    shown = <a href={value} target="_blank" rel="noreferrer">{value}</a>
  }
  return <Fact label={field.label} value={shown} />
}

/** Frappe stores a Text Editor's HTML; notes and comments are read as text
 *  here. DOMParser, not innerHTML on an element: a detached element still runs
 *  an <img onerror>, a parsed document does not. */
function stripTags(html: string): string {
  return new DOMParser().parseFromString(html, 'text/html').body.textContent ?? ''
}

/* ── the step that is waiting ─────────────────────────────────────────── */

function NextStep({ detail, kind, onDone, nameOf }: {
  detail: WorkDetail; kind: Kind; onDone: () => void; nameOf: (e: string) => string
}) {
  const erp = useErp()
  const toast = useToast()
  const [open, setOpen] = useState<WorkStep | null>(null)
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [hours, setHours] = useState('')
  const [reason, setReason] = useState<unknown>('')
  const [note, setNote] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [sending, setSending] = useState(false)
  const [key, setKey] = useState(newIdempotencyKey)
  const t = detail.task
  const words = kind.words

  useEffect(() => {
    // A fresh form for each step, starting from what the task already holds.
    const start: Record<string, unknown> = {}
    for (const f of kind.reply?.fields ?? []) {
      const v = t[f.fieldname]
      if (v !== null && v !== undefined && v !== '') start[f.name] = v
    }
    setValues(start); setReason(''); setNote(''); setErrors({})
    // "Both": the box starts with the timer's time, to correct if need be.
    setHours(detail.time_mode !== 'manual' && detail.timer?.task === t.name
      ? String(hoursSince(detail.timer.started_at)) : '')
    setKey(newIdempotencyKey())
  }, [open, kind, t, detail.timer, detail.time_mode])

  if (!detail.steps.length) {
    const closed = t.closed
    if (closed) return null
    const waiting = t.workflow_state === STATES.WITH_CHECKER ? t.jd_checker
      : t.workflow_state === STATES.WITH_REVIEWER ? t.jd_reviewer
      : t.workflow_state === STATES.WITH_APPROVER ? t.jd_approver
      : kind.flow === 'decide' ? t.jd_approver
      : t.assigned[0]
    return kind.flow ? (
      <div className="card card-pad small muted" role="status">
        Nothing is waiting on you here.{waiting ? ` It is with ${nameOf(waiting)}.` : ''}
      </div>
    ) : null
  }

  const needs = open ? stepNeeds(open) : 'nothing'
  const reply = kind.reply
  // Where this person's hours go is the gateway's answer (a designer's are design time); an
  // older gateway does not say, and the reply's own activity stands in.
  const activity = detail.hours_activity
    ?? (reply ? (t.jd_round && reply.hours_on_revision ? reply.hours_on_revision : reply.hours) : undefined)

  async function go() {
    if (!open) return
    if (needs === 'note' && !note.trim()) {
      setErrors({ note: open.action === 'Reject' ? 'Say why.' : 'Say what needs changing.' })
      return
    }
    setSending(true); setErrors({})
    try {
      const body: Parameters<typeof erp.workStep>[1] = { action: open.action }
      if (needs === 'reply' && reply && Object.keys(values).length) body.values = values
      if (needs === 'reply' && hours.trim()) body.hours = Number(hours)
      if (needs === 'reason') body.reason = String(reason || '')
      if (note.trim()) body.note = note.trim()
      const done = await erp.workStep(detail.task.name, body, key)
      toast('ok', `${stepLabel(open, words)}: done.`
        + (done.job === 'Completed' ? ' Every row is finished, so the job is closed.' : ''))
      if (done.hours_not_saved) toast('error', `The hours were not logged: ${done.hours_not_saved}`)
      if (done.timer?.timesheet) toast('ok', `Your timer logged ${done.timer.hours} h.`)
      setOpen(null)
      onDone()
    } catch (e) {
      const error = e as ErpError
      setErrors({ ...(error.fields ?? {}), _: error.message })
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="card card-pad" aria-label="Your next step"
             style={{ display: 'grid', gap: 12, borderColor: 'var(--accent-line)' }}>
      <div className="row row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <strong>{t.needs_me ? `Your turn as ${roleWord(whoWord(t.needs_me, words))}` : 'What you can do'}</strong>
      </div>
      {!open ? (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {detail.steps.map((s) => (
            <button key={s.action} className={`btn ${s.action === detail.steps[0]?.action ? 'btn-primary' : ''}`}
                    onClick={() => setOpen(s)}>
              {stepLabel(s, words)}
            </button>
          ))}
        </div>
      ) : null}

      {open ? (
        <form style={{ display: 'grid', gap: 12 }} onSubmit={(e) => { e.preventDefault(); void go() }}
              noValidate aria-label={stepLabel(open, words)}>
          <h3 style={{ margin: 0 }}>{stepLabel(open, words)}</h3>
          {needs === 'reply' && reply ? (
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              {reply.fields.map((f) => (
                <PackFieldInput key={f.name} field={f} value={values[f.name] ?? ''} error={errors[f.name]}
                                onChange={(v) => setValues((vs) => ({ ...vs, [f.name]: v }))} />
              ))}
              {activity && detail.time_mode === 'automatic' ? (
                <div className="small muted" style={{ alignSelf: 'end' }}>
                  Time here is recorded by your timer
                  {detail.timer?.task === t.name ? ': sending this stops it and logs it.' : ' — start it when you begin.'}
                </div>
              ) : activity ? (
                <div className="field">
                  <label htmlFor="step_hours">Hours spent{activity ? ` (${activity.toLowerCase()})` : ''}</label>
                  <input id="step_hours" className="input" inputMode="decimal" value={hours}
                         placeholder="e.g. 2.5" aria-invalid={errors.hours ? true : undefined}
                         onChange={(e) => setHours(e.target.value.replace(/[^0-9.]/g, ''))} />
                  <div className="small muted">
                    {detail.timer?.task === t.name ? 'From your timer — change it if need be. ' : ''}
                    Goes on your timesheet against this task.
                  </div>
                  {errors.hours ? <div className="small" style={{ color: 'var(--danger)' }}>{errors.hours}</div> : null}
                </div>
              ) : null}
            </div>
          ) : null}
          {needs === 'reason' && reply?.reason ? (
            <PackFieldInput field={reply.reason} value={reason} required error={errors.reason}
                            onChange={setReason} />
          ) : null}
          <div className="field">
            <label htmlFor="step_note">
              {needs === 'note' ? (open.action === 'Reject' ? 'Why' : 'What needs changing')
                : needs === 'reason' ? 'What exactly' : 'Note'}
              {needs === 'note' ? <span aria-hidden="true"> *</span> : null}
            </label>
            <textarea id="step_note" className="textarea" rows={3} value={note}
                      aria-invalid={errors.note ? true : undefined}
                      onChange={(e) => setNote(e.target.value)} />
            {errors.note ? <div className="small" style={{ color: 'var(--danger)' }}>{errors.note}</div> : null}
          </div>
          {needs === 'reply' ? (
            <div className="small muted">Attach drawings and files under Files below, before or after sending.</div>
          ) : null}
          {errors._ ? <div role="alert" className="small" style={{ color: 'var(--danger)' }}>{errors._}</div> : null}
          <div className="row" style={{ gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={sending}>
              {sending ? 'Sending…' : stepLabel(open, words)}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setOpen(null)} disabled={sending}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </section>
  )
}

/* ── around it ───────────────────────────────────────────────────────── */

/** A job's approved work as a draft invoice: every line with its row, the
 *  client's prices, and what is missing named with where to set it. Shown to
 *  people who may raise invoices; an accountant finishes the draft in ERPNext. */
function Invoice({ name, onOpenInvoice }: { name: string; onOpenInvoice?: (n: string) => void }) {
  const erp = useErp()
  const toast = useToast()
  const [draft, setDraft] = useState<InvoiceDraft | null>(null)
  const [hidden, setHidden] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [key, setKey] = useState(newIdempotencyKey)
  const load = useCallback(() => {
    erp.workInvoice(name).then((d) => { setDraft(d); setNote(null) }).catch((e) => {
      const error = e as ErpError
      if (error.status === 403) setHidden(true)
      else setNote(error.message)          // e.g. the project has no client yet
    })
  }, [erp, name])
  useEffect(load, [load])
  if (hidden) return null
  if (note && !draft) {
    return <section className="card card-pad small" aria-label="Invoice"><strong>Invoice</strong> · {note}</section>
  }
  if (!draft) return null
  const blocked = draft.missing_items.length > 0 || draft.missing_prices.length > 0
  return (
    <section className="card" aria-label="Invoice" style={{ overflowX: 'auto' }}>
      <div className="card-pad row row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <strong>Invoice · {draft.customer}</strong>
          <div className="small muted">Prices from “{draft.price_list}”. {draft.about}</div>
        </div>
        <button className="btn btn-primary" disabled={busy || blocked || !draft.lines.length}
                onClick={async () => {
                  setBusy(true)
                  try {
                    const made = await erp.raiseWorkInvoice(name, key)
                    toast('ok', `Draft ${made.invoice} raised: ${made.lines} lines, ${formatNumber(made.total, 2)}.`)
                    setKey(newIdempotencyKey()); load()
                    onOpenInvoice?.(made.invoice)
                  } catch (e) { toast('error', (e as ErpError).message) }
                  finally { setBusy(false) }
                }}>
          {busy ? 'Raising…' : 'Raise draft invoice'}
        </button>
      </div>
      {draft.missing_items.length ? (
        <div className="card-pad small" role="status" style={{ paddingTop: 0, color: 'var(--danger)' }}>
          Not set up as items yet: {draft.missing_items.join(', ')}. Each needs its HSN/SAC code: add them under
          Items, or give the codes when installing the pack again (Set up → Industry packs).
        </div>
      ) : null}
      {draft.missing_prices.length ? (
        <div className="card-pad small" role="status" style={{ paddingTop: 0, color: 'var(--danger)' }}>
          No price for {draft.missing_prices.join(', ')} in “{draft.price_list}”. Set them under Set up → Price lists.
        </div>
      ) : null}
      {draft.lines.length ? (
        <table className="tbl">
          <thead><tr><th>Row</th><th>Item</th><th className="num">Qty</th><th className="num">Rate</th><th className="num">Amount</th></tr></thead>
          <tbody>
            {draft.lines.map((l, i) => (
              <tr key={i} style={{ cursor: 'default' }}>
                <td>{l.row_subject}</td>
                <td>{l.item}</td>
                <td className="num">{formatNumber(l.qty, 2)} {l.uom}</td>
                <td className="num">{l.rate === null ? '—' : formatNumber(l.rate, 2)}</td>
                <td className="num">{formatNumber(l.amount, 2)}</td>
              </tr>
            ))}
            <tr style={{ cursor: 'default' }}>
              <td colSpan={4}><b>Total before tax</b></td>
              <td className="num"><b>{formatNumber(draft.total, 2)}</b></td>
            </tr>
          </tbody>
        </table>
      ) : (
        <div className="card-pad small muted" style={{ paddingTop: 0 }}>
          Nothing to bill yet: rows are billed once approved, and only once.
        </div>
      )}
      {draft.billed.length || draft.not_approved.length ? (
        <div className="card-pad small" style={{ display: 'grid', gap: 4 }}>
          {draft.billed.length ? (
            <span>Already billed: {draft.billed.map((b) => (
              <button key={b.row} className="btn btn-quiet btn-sm" onClick={() => onOpenInvoice?.(b.invoice)}>
                {b.subject} → {b.invoice}
              </button>
            ))}</span>
          ) : null}
          {draft.not_approved.length ? (
            <span className="muted">Not approved yet, so not on this invoice: {draft.not_approved.map((r) => r.subject).join(', ')}.</span>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

/** Sending approved work to the client: the files on it, a note, and the
 *  revision number the server keeps. Hidden for people who may not release. */
function Release({ name, onChanged, onSendFiles }: {
  name: string; onChanged: () => void
  /** The host's channels (the desk: WhatsApp, a connected inbox); absent where there are none (PK-42). */
  onSendFiles?: (release: ReleaseSend) => Promise<SentFile[]>
}) {
  const erp = useErp()
  const toast = useToast()
  const [state, setState] = useState<ReleaseState | null>(null)
  const [by, setBy] = useState<'email' | 'chat'>('email')
  const [hidden, setHidden] = useState(false)
  const [to, setTo] = useState<string[]>([])
  const [other, setOther] = useState('')
  const [files, setFiles] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [key, setKey] = useState(newIdempotencyKey)

  const load = useCallback(() => {
    erp.workRelease(name).then((r) => {
      setState(r)
      setFiles(r.files.map((f) => f.name))
      setTo((now) => (now.length ? now : r.suggest.slice(0, 1).map((c) => c.email)))
    }).catch((e) => { if ((e as ErpError).status === 403) setHidden(true) })
  }, [erp, name])
  useEffect(load, [load])

  if (hidden || !state) return null
  const label = state.rev ? `Send Rev ${state.rev} to the client` : 'Send to the client'
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

  return (
    <section className="card card-pad" aria-label="Release to the client" style={{ display: 'grid', gap: 10 }}>
      <div className="row row-between" style={{ flexWrap: 'wrap', gap: 6 }}>
        <strong>Release to the client{state.client ? ` · ${state.client}` : ''}</strong>
        <span className="small muted">{state.subject}</span>
      </div>
      {onSendFiles ? (
        <div className="row" role="radiogroup" aria-label="Send it by" style={{ gap: 12, flexWrap: 'wrap' }}>
          <label className="check small"><input type="radio" checked={by === 'chat'} onChange={() => setBy('chat')} />
            On your channels: WhatsApp, or your connected inbox</label>
          <label className="check small"><input type="radio" checked={by === 'email'} onChange={() => setBy('email')} />
            By email from the books</label>
        </div>
      ) : null}
      {by === 'email' && !state.can_send ? (
        <div className="small" role="status">
          No email account is set up to send from yet. Add one under Set up → Email accounts{onSendFiles ? ', or send it on your channels.' : '.'}
        </div>
      ) : null}
      {by === 'chat' ? (
        <div className="small muted">
          To {state.client || 'the client'}: you pick the channel next. Each file goes as it is, with your note.
        </div>
      ) : null}
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: by === 'chat' ? 'none' : 'grid', gap: 4 }}>
        <legend className="small muted">To</legend>
        {state.suggest.map((c) => (
          <label key={c.email} className="check small">
            <input type="checkbox" checked={to.includes(c.email)} onChange={() => setTo(toggle(to, c.email))} />
            {c.name} — {c.email}
          </label>
        ))}
        {!state.suggest.length ? (
          <span className="small muted">
            {state.client ? `${state.client} has no contact with an email yet.` : 'This job’s project has no client.'}
            {' '}Type the address below.
          </span>
        ) : null}
        <label htmlFor={`rel_other_${name}`} className="sr-only">Other addresses</label>
        <input id={`rel_other_${name}`} className="input" placeholder="Other addresses, separated by commas"
               value={other} onChange={(e) => setOther(e.target.value)} />
      </fieldset>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 4 }}>
        <legend className="small muted">Files</legend>
        {state.files.length ? state.files.map((f) => (
          <label key={f.name} className="check small">
            <input type="checkbox" checked={files.includes(f.name)} onChange={() => setFiles(toggle(files, f.name))} />
            {f.file_name || f.name}
          </label>
        )) : <span className="small muted">Nothing attached yet. Attach the files under Files below first.</span>}
      </fieldset>
      <div className="field">
        <label htmlFor={`rel_note_${name}`}>Covering note</label>
        <textarea id={`rel_note_${name}`} className="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {problem ? <div role="alert" className="small" style={{ color: 'var(--danger)' }}>{problem}</div> : null}
      <div>
        <button className="btn btn-primary"
                disabled={sending || !files.length || (by === 'email' && (!state.can_send || (!to.length && !other.trim())))}
                onClick={async () => {
                  setSending(true); setProblem(null)
                  try {
                    if (by === 'chat' && onSendFiles) {
                      const chosen = state.files.filter((f) => files.includes(f.name))
                      const sent = await onSendFiles({
                        task: name, client: state.client, subject: state.subject, rev: state.rev, note: note.trim(),
                        reach: state.reach ?? null,
                        files: chosen.map((f) => ({ name: f.name, file_name: f.file_name || f.name,
                                                    fetch: () => erp.releaseFile(name, f.name) })),
                      })
                      const done = await erp.releaseWork(name, { channel: 'chat', sent, note: note.trim() || undefined, files }, key)
                      toast('ok', `${done.rev ? `Rev ${done.rev}` : 'The first issue'} went to ${done.to.join(', ')}.`)
                      setNote(''); setKey(newIdempotencyKey()); load(); onChanged()
                      return
                    }
                    const extra = other.split(/[,;]/).map((x) => x.trim()).filter(Boolean)
                    const done = await erp.releaseWork(name, { to: [...to, ...extra], note: note.trim() || undefined, files }, key)
                    toast('ok', `${done.rev ? `Rev ${done.rev}` : 'The first issue'} went to ${done.to.join(', ')}.`)
                    setNote(''); setOther(''); setKey(newIdempotencyKey()); load(); onChanged()
                  } catch (e) { if ((e as Error).name !== 'AbortError') setProblem((e as ErpError).message) }
                  finally { setSending(false) }
                }}>
          {sending ? 'Sending…' : by === 'chat' ? `${label} on your channels` : label}
        </button>
      </div>
      {state.history.length ? (
        <div className="small" style={{ display: 'grid', gap: 2 }}>
          <span className="muted">Sent before</span>
          {state.history.map((h) => (
            <span key={h.name}>{formatDatetime(h.communication_date)} · {h.subject} · to {h.recipients}</span>
          ))}
        </div>
      ) : null}
    </section>
  )
}

/** Where this job's project posts a card each time its work moves. Shown
 *  only when the organisation has connected Teams. */
function TeamsChannel({ name, current, onChanged }: { name: string; current: string; onChanged: () => void }) {
  const erp = useErp()
  const toast = useToast()
  const [teams, setTeams] = useState<TeamsConnection | null>(null)
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    const controller = new AbortController()
    erp.teams(controller.signal).then(setTeams).catch(() => setTeams(null))
    return () => controller.abort()
  }, [erp])
  if (!teams?.connected) return null
  const known = teams.channels.some((c) => c.id === current)
  return (
    <section className="card card-pad row" aria-label="Teams" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      <label htmlFor="teams_channel" className="small" style={{ fontWeight: 600 }}>Cards in Teams</label>
      <select id="teams_channel" className="select" style={{ maxWidth: 320 }} value={current} disabled={saving}
              onChange={async (e) => {
                setSaving(true)
                try {
                  await erp.setWorkChannel(name, e.target.value)
                  toast('ok', e.target.value ? 'Each step on this project now posts a card there.' : 'No more cards for this project.')
                  onChanged()
                } catch (err) { toast('error', (err as ErpError).message) }
                finally { setSaving(false) }
              }}>
        <option value="">Not posted</option>
        {current && !known ? <option value={current}>{current} (the app is no longer in it)</option> : null}
        {teams.channels.map((c) => <option key={c.id} value={c.id}>{c.name === c.id ? c.id : `#${c.name}`}</option>)}
      </select>
      {!teams.channels.length ? (
        <span className="small muted">Add the app to a channel in Teams first, then choose it here.</span>
      ) : <span className="small muted">Each step on this project's work posts a card in that channel.</span>}
    </section>
  )
}

/** A job's rows: who, where it stands and when, and each of the pack's row fields in a column
 *  of its own (it was one run-on sentence). A field no row has said anything for is left out. */
function Rows({ rows, kindOf, title, onOpen, nameOf, mayPlan, onChanged }: {
  rows: WorkRow[]; kindOf: (r: WorkRow) => Kind | undefined; title: string
  onOpen: (n: string) => void; nameOf: (e: string) => string
  /** May this person change who does them (the job's planner)? */
  mayPlan?: boolean
  onChanged?: () => void
}) {
  const [changing, setChanging] = useState<string | null>(null)
  const said = (v: unknown) => v !== null && v !== undefined && v !== '' && v !== 0 && v !== '0'
  const k0 = rows[0] ? kindOf(rows[0]) : undefined
  const fields = (k0?.fields ?? []).filter((f) => rows.some((r) => said(r[f.fieldname])))
  const done = rows.filter((r) => r.workflow_state === STATES.APPROVED).length
  const reviewed = rows.some((r) => r.jd_reviewer)
  const show = (f: PackField, v: unknown): ReactNode => {
    if (!said(v)) return <span className="muted">—</span>
    if (f.fieldtype === 'Check') return 'Yes'
    if (f.fieldtype === 'Float') return formatNumber(v)
    if (f.fieldtype === 'Date') return formatDate(v)
    return String(v)
  }
  return (
    <section className="card" aria-label={title} style={{ overflowX: 'auto' }}>
      <div className="card-pad" style={{ paddingBottom: 8 }}>
        <strong>{title}</strong> <span className="small muted">· {rows.length}{done ? `, ${done} approved` : ''}</span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>{k0?.label ?? 'Row'}</th>
            <th>{whoWord('maker', k0?.words)}</th>
            <th>{whoWord('checker', k0?.words)}</th>
            {reviewed ? <th>{whoWord('reviewer', k0?.words)}</th> : null}
            <th>State</th>
            <th>{k0?.words?.deadline ?? 'Deadline'}</th>
            {fields.map((f) => <th key={f.name} className={f.fieldtype === 'Float' ? 'num' : undefined}>{f.label}</th>)}
            {mayPlan ? <th><span className="sr-only">Reassign</span></th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const k = kindOf(r)
            return [
              <tr key={r.name} tabIndex={0} onClick={() => onOpen(r.name)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOpen(r.name) }}>
                <td><b>{r.subject}</b>{r.jd_round ? <div className="small muted">revision {r.jd_round}</div> : null}</td>
                <td className="small">{r.assigned.map(nameOf).join(', ') || <span className="muted">—</span>}</td>
                <td className="small">{r.jd_checker ? nameOf(r.jd_checker) : <span className="muted">—</span>}</td>
                {reviewed ? <td className="small">{r.jd_reviewer ? nameOf(r.jd_reviewer) : <span className="muted">—</span>}</td> : null}
                <td><span className={`chip chip-${stateTone(r.workflow_state)}`}>
                  {stateLabel(r.workflow_state, k?.words, k?.flow)}</span></td>
                <td className="small" style={r.overdue ? { color: 'var(--danger)' } : undefined}>
                  {r.exp_end_date ? formatDate(r.exp_end_date) : '—'}</td>
                {fields.map((f) => (
                  <td key={f.name} className={`small${f.fieldtype === 'Float' ? ' num' : ''}`}>{show(f, r[f.fieldname])}</td>
                ))}
                {mayPlan ? (
                  <td>{!r.closed && k ? (
                    <button className="btn btn-quiet btn-sm" aria-expanded={changing === r.name}
                            onClick={(e) => { e.stopPropagation(); setChanging(changing === r.name ? null : r.name) }}
                            onKeyDown={(e) => e.stopPropagation()}>Reassign</button>
                  ) : null}</td>
                ) : null}
              </tr>,
              changing === r.name && k ? (
                <tr key={`${r.name}-people`} className="people-change-row">
                  <td colSpan={6 + (reviewed ? 1 : 0) + fields.length}>
                    <PeopleChange row={r} kind={k} onCancel={() => setChanging(null)}
                                  onDone={() => { setChanging(null); onChanged?.() }} />
                  </td>
                </tr>
              ) : null,
            ]
          })}
        </tbody>
      </table>
    </section>
  )
}

function Files({ detail, onChanged }: { detail: WorkDetail; onChanged: () => void }) {
  const erp = useErp()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  async function add(files: FileList | null) {
    if (!files?.length) return
    setBusy(true)
    try {
      for (const file of Array.from(files)) {
        const body = new FormData()
        body.set('file', file)
        body.set('doctype', 'Task')
        body.set('name', detail.task.name)
        await erp.upload(body)
      }
      toast('ok', files.length === 1 ? 'File attached.' : `${files.length} files attached.`)
      onChanged()
    } catch (e) {
      toast('error', (e as ErpError).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="card card-pad" aria-label="Files" style={{ display: 'grid', gap: 8 }}>
      <div className="row row-between">
        <strong>Files</strong>
        <label className="btn btn-sm" style={{ cursor: busy ? 'progress' : 'pointer' }}>
          {busy ? 'Attaching…' : 'Attach files'}
          <input type="file" multiple className="sr-only" disabled={busy}
                 onChange={(e) => { void add(e.target.files); e.target.value = '' }} />
        </label>
      </div>
      {detail.files.length ? detail.files.map((f) => (
        <a key={f.name} className="truncate small" href={f.file_url} target="_blank" rel="noreferrer">
          {f.file_name || f.file_url}
        </a>
      )) : <div className="small muted">No files yet. Drawings, bar lists and mails go here.</div>}
    </section>
  )
}

function Hours({ detail, nameOf }: { detail: WorkDetail; nameOf: (e: string) => string }) {
  return (
    <section className="card" aria-label="Hours" style={{ overflowX: 'auto' }}>
      <div className="card-pad" style={{ paddingBottom: 0 }}>
        <strong>Hours</strong> <span className="small muted">· {formatNumber(detail.total_hours, 2)} in all</span>
      </div>
      <table className="tbl">
        <thead><tr><th>Who</th><th>For</th><th className="num">Hours</th><th>When</th></tr></thead>
        <tbody>
          {detail.hours.map((h, i) => (
            <tr key={`${h.parent}-${i}`} style={{ cursor: 'default' }}>
              <td>{nameOf(h.owner ?? '')}</td>
              <td>{h.activity_type ?? ''}</td>
              <td className="num">{formatNumber(h.hours, 2)}</td>
              <td className="small muted">{formatDatetime(h.from_time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

function Conversation({ detail, onChanged, nameOf }: {
  detail: WorkDetail; onChanged: () => void; nameOf: (e: string) => string
}) {
  const erp = useErp()
  const toast = useToast()
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const comments = [...detail.comments].sort((a, b) => String(a.at).localeCompare(String(b.at)))
  return (
    <section className="card card-pad" aria-label="Conversation" style={{ display: 'grid', gap: 10 }}>
      <strong>Conversation</strong>
      {comments.length ? comments.map((c, i) => (
        <div key={i} style={{ borderLeft: '3px solid var(--line)', paddingLeft: 10 }}>
          <div className="small muted">{nameOf(c.by ?? '')} · {formatDatetime(c.at)}</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{stripTags(c.content ?? '')}</div>
        </div>
      )) : <div className="small muted">Nothing said yet. Questions and answers stay here, with the task.</div>}
      <form className="row" style={{ gap: 8, alignItems: 'flex-end' }}
            onSubmit={async (e) => {
              e.preventDefault()
              if (!text.trim()) return
              setSending(true)
              try { await erp.addComment('Task', detail.task.name, text.trim()); setText(''); onChanged() }
              catch (err) { toast('error', (err as ErpError).message) }
              finally { setSending(false) }
            }}>
        <div className="field grow">
          <label htmlFor="comment_box" className="sr-only">Add a comment</label>
          <textarea id="comment_box" className="textarea" rows={2} placeholder="Add a comment"
                    value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <button className="btn" disabled={sending || !text.trim()}>{sending ? 'Sending…' : 'Send'}</button>
      </form>
    </section>
  )
}
